// Dependencies
import React, { useState, useEffect, useMemo } from "react";

// Modules

// App
import { apiEndpoints } from "../../config";

import PostEdit from "../../partials/posts/postEdit";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ContactsIcon from "@mui/icons-material/ContactsOutlined";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import MailIcon from "@mui/icons-material/EmailOutlined";
import AddIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Row, Col } from "react-bootstrap";
import { Typography, TextField, Button, IconButton } from "@mui/material";

// CompanyDirectoryCrudResource rejects anything longer as a 400 — the shared
// paragraph storage caps the column at 15.
const PHONE_MAX_LENGTH = 15;

const emptyContactGroup = () => ({
  field_section_title: "",
  field_phone_number: "",
  field_email: "",
});

export default function CompanyDirectoryEdit() {
  const [officeHours, setOfficeHours] = useState(null);
  const [address, setAddress] = useState("");
  const [contactGroups, setContactGroups] = useState([]);
  // The CRUD resource REPLACES every paragraph with whatever list it is sent,
  // so field_contact_group must stay out of the payload until the existing
  // groups have actually loaded — otherwise saving any other field on a
  // still-loading form would wipe them. Once loaded, an empty list is a
  // legitimate "the editor deleted them all".
  const [contactGroupsLoaded, setContactGroupsLoaded] = useState(false);
  const [post, setPost] = useState(null);

  const extraFields = useMemo(() => {
    const _officeHours = officeHours
      ? { field_opening_times: officeHours }
      : {};
    const _address = address ? { field_address: address } : {};
    const _contactGroups = contactGroupsLoaded
      ? {
          field_contact_group: contactGroups
            // A row the editor added but never filled in would otherwise be
            // saved as an empty paragraph.
            .filter(
              (group) =>
                group.field_section_title.trim() ||
                group.field_phone_number.trim() ||
                group.field_email.trim(),
            )
            .map((group) => ({
              field_section_title: group.field_section_title.trim(),
              field_phone_number: group.field_phone_number.trim(),
              field_email: group.field_email.trim(),
            })),
        }
      : {};
    return {
      ..._officeHours,
      ..._address,
      ..._contactGroups,
    };
  }, [officeHours, address, contactGroups, contactGroupsLoaded]);

  useEffect(() => {
    if (!post) return;
    if (post.field_opening_times) {
      setOfficeHours(post.field_opening_times);
    }
    if (post.field_address) {
      setAddress(post.field_address);
    }
    setContactGroups(
      (Array.isArray(post.field_contact_group)
        ? post.field_contact_group
        : []
      ).map((group) => ({
        field_section_title: group?.field_section_title ?? "",
        field_phone_number: group?.field_phone_number ?? "",
        field_email: group?.field_email ?? "",
      })),
    );
    setContactGroupsLoaded(true);
  }, [post]);

  const updateContactGroup = (index, field, value) =>
    setContactGroups((groups) =>
      groups.map((group, i) =>
        i === index ? { ...group, [field]: value } : group,
      ),
    );

  const addContactGroup = () =>
    setContactGroups((groups) => [...groups, emptyContactGroup()]);

  const removeContactGroup = (index) =>
    setContactGroups((groups) => groups.filter((_, i) => i !== index));

  // The icon + bold label column the office hours / location rows already use,
  // so the contact rows line up with them instead of inventing a second style.
  const fieldLabel = (labelString, icon) => (
    <Col xs={5} sm={3}>
      <Row className="ps-4 ms-2">
        <Col xs={"auto"} className="pe-3">
          {icon}
        </Col>
        <Col className="px-0">
          <Typography
            owner="client"
            variant="body2"
            color="clientText.main"
            lh="x-wide"
            weight="bold"
            singles="true"
          >
            {labelString}
          </Typography>
        </Col>
      </Row>
    </Col>
  );

  return (
    <PostEdit
      entityType={"company_directory"}
      apiEndpoint={apiEndpoints.company_directory}
      screenClassName={"company-directory"}
      categoryTitle={"Company Directory category"}
      categoryName={"field_company-directory_category"}
      targetVid={"company_directory_categories"}
      returnRoute={"/company_directory-creator"}
      backBtnText={"Company Directory Library"}
      previewRoute={"/company_directory/preview"}
      logCustomEventType={"company_directory"}
      featuredImageEnabled={true}
      extraFields={extraFields}
      featuredImageFiles={{
        aspectRatio: 1,
      }}
      setEntityPost={(_post) => {
        setPost(_post);
      }}
      editorProps={{
        title: true,
        subtitle: true,
        body: true,
        list: true,
        table: true,
        image: true,
        video: true,
        embed: true,
        rule: true,
        buttonGroup: true,
        chart: true,
        map: true,
        contactGroup: true,
      }}
      imageBlockEnabled={true}
    >
      <Row className="my-3">
        <Col xs={5} sm={3}>
          <Row className="ps-4 ms-2">
            <Col xs={"auto"} className="pe-3">
              <AccessTimeIcon color="clientPrimary" />
            </Col>
            <Col className="px-0">
              <Typography
                owner="client"
                variant="body2"
                color="clientText.main"
                lh="x-wide"
                weight="bold"
                singles="true"
              >
                Office hours:
              </Typography>
            </Col>
          </Row>
        </Col>

        <Col xs={7} sm={9} className="flex-grow-1 ps-4">
          <Row>
            <Col>
              <TextField
                className="w-100"
                label="Enter office hours"
                variant="outlined"
                value={officeHours}
                onChange={(e) => setOfficeHours(e.target.value)}
                size="small"
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col xs={5} sm={3}>
          <Row className="ps-4 ms-2">
            <Col xs={"auto"} className="pe-3">
              <LocationOnOutlinedIcon color="clientPrimary" />
            </Col>
            <Col className="px-0">
              <Typography
                owner="client"
                variant="body2"
                color="clientText.main"
                lh="x-wide"
                weight="bold"
                singles="true"
              >
                Location:
              </Typography>
            </Col>
          </Row>
        </Col>

        <Col xs={7} sm={9} className="flex-grow-1 ps-4">
          <Row>
            <Col>
              <TextField
                className="w-100"
                label="Enter address"
                variant="outlined"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                size="small"
              />
            </Col>
          </Row>
        </Col>
      </Row>

      {contactGroups.map((group, index) => (
        <React.Fragment key={index}>
          <Row className="mb-3 align-items-center">
            {fieldLabel(
              "Contact group:",
              <ContactsIcon color="clientPrimary" />,
            )}
            <Col xs={7} sm={9} className="flex-grow-1 ps-4">
              <Row className="align-items-center flex-nowrap">
                <Col>
                  <TextField
                    className="w-100"
                    label="Enter section title"
                    variant="outlined"
                    value={group.field_section_title}
                    onChange={(e) =>
                      updateContactGroup(
                        index,
                        "field_section_title",
                        e.target.value,
                      )
                    }
                    size="small"
                  />
                </Col>
                <Col xs={"auto"} className="ps-0">
                  <IconButton
                    aria-label={`Remove contact group ${index + 1}`}
                    onClick={() => removeContactGroup(index)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="mb-3">
            {fieldLabel("Phone:", <PhoneIcon color="clientPrimary" />)}
            <Col xs={7} sm={9} className="flex-grow-1 ps-4">
              <Row>
                <Col>
                  <TextField
                    className="w-100"
                    label="Enter phone number"
                    variant="outlined"
                    value={group.field_phone_number}
                    onChange={(e) =>
                      updateContactGroup(
                        index,
                        "field_phone_number",
                        e.target.value,
                      )
                    }
                    inputProps={{ maxLength: PHONE_MAX_LENGTH }}
                    helperText={`${PHONE_MAX_LENGTH} characters max`}
                    size="small"
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="mb-3">
            {fieldLabel("Email:", <MailIcon color="clientPrimary" />)}
            <Col xs={7} sm={9} className="flex-grow-1 ps-4">
              <Row>
                <Col>
                  <TextField
                    className="w-100"
                    label="Enter email address"
                    variant="outlined"
                    type="email"
                    value={group.field_email}
                    onChange={(e) =>
                      updateContactGroup(index, "field_email", e.target.value)
                    }
                    size="small"
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </React.Fragment>
      ))}

      <Row className="mb-3">
        <Col xs={12} className="ps-4 ms-2">
          <Button
            owner="client"
            variant="text"
            startIcon={<AddIcon />}
            onClick={addContactGroup}
          >
            Add contact group
          </Button>
        </Col>
      </Row>
    </PostEdit>
  );
}
